import { ProductRepository, CategoryRepository } from "core/repository";
import { Base2Presenter } from "core/presenter";
import { fn } from "util";
import _ from "lodash";

class DataTablePresenter extends Base2Presenter {
  static sNoResultsFilter = [
    { field: "deleted", operation: "EQ", value: true },
    { field: "deleted", operation: "EQ", value: false }
  ];

  initialState() {
    return {
      loading: true,
      resultSet: [],
      pagination: {
        perPage: 25,
        currentPage: 1,
        total: 0
      },
      order: {
        field: "name",
        direction: "ASC"
      },
      filters: {
        status: "active",
        stock: "all",
        category: {
          uuid: "",
          name: ""
        }
      },
      visible: {},
      printPdf: {},
      printAmount: 1,
      checkedProducts: {},
      checkboxCount: 0,
      anchor: null,
      open: false,
      renderButton: false,
      renderSelectAllButton: false,
      allProductsSelected: false,
      batchCategoryDialogOpen: false,
      categorySearchOptions: {
        repository: CategoryRepository,
        order: [{ field: "name", direction: "ASC" }],
        pagination: { page: 1, perPage: 25 },
        filter: filter => this.getCategoryFilters(filter)
      },
      batchFiscalInformationsDialogOpen: false
    };
  }

  onStart() {
    this.doLoadResults();
  }

  onOpenModal(uuid) {
    this.enqueue(vm => ({
      visible: {
        ...vm.visible,
        [uuid]: true
      }
    }));
  }

  onCloseModal(uuid) {
    this.enqueue(vm => ({
      visible: {
        ...vm.visible,
        [uuid]: false
      }
    }));
    this.onPrintChanged(uuid, false);
  }

  onPrintChanged(uuid, value) {
    this.enqueue(vm => ({
      printPdf: {
        ...vm.printPdf,
        [uuid]: value
      }
    }));
  }

  onRequestEdit(sellable) {
    this.navigateTo("app.registration.product.edit", {
      productUuid: sellable.uuid
    });
  }

  onRequestView(sellable) {
    this.navigateTo("app.registration.product.info", {
      productUuid: sellable.uuid
    });
  }

  async doLoadResults() {
    const filters = await this.getFilters();

    this.enqueue({ loading: true });
    this.renewCancelToken();

    const repositoryResult = await ProductRepository.getListOnline({
      order: [this.viewModel.order],
      pagination: {
        page: this.viewModel.pagination.currentPage,
        perPage: this.viewModel.pagination.perPage
      },
      filters: filters
    });

    if (repositoryResult.success) {
      const resultSet = repositoryResult.data;

      const resultSetPromises = resultSet.map(async result => {
        if (!result.category_uuid) {
          result.category_name = "-";
          return result;
        }

        result.category_name = result.category.name;
        return result;
      });

      await Promise.all(resultSetPromises);

      this.enqueue(
        vm => ({
          resultSet,
          pagination: {
            ...vm.pagination,
            total: repositoryResult.total,
            perPage: repositoryResult.perPage
          }
        }),
        () => this.enqueue({ loading: false })
      );
    } else if (!repositoryResult.userCancelled) {
      this.sendNotification({
        type: "error",
        title: "Ocorreu um erro",
        content:
          "Não foi possível carregar os dados. Tente novamente mais tarde."
      });
      this.enqueue({ loading: false });
    }
  }

  async getFilters() {
    const { filters } = this.viewModel;
    const currentUser = await this.mRuntime.get("currentUser");

    const repositoryFilters = [
      {
        connector: "AND",
        field: "company_uuid",
        operation: "EQ",
        value: currentUser.company_uuid
      }
    ];

    if (_.isNull(filters.category.uuid)) {
      repositoryFilters.push({
        field: "category_uuid",
        operation: "EQ",
        value: "!null!"
      });
    } else if (!fn.isBlank(filters.category.uuid)) {
      repositoryFilters.push({
        field: "category_uuid",
        operation: "EQ",
        value: filters.category.uuid
      });
    }

    if (!fn.isBlank(filters.code)) {
      repositoryFilters.push({
        field: "code",
        operation: "LIKE",
        value: filters.code
      });
    }

    if (!fn.isBlank(filters.name)) {
      repositoryFilters.push({
        field: "name",
        operation: "LIKE",
        value: filters.name
      });
    }

    if (filters.status !== "all") {
      repositoryFilters.push({
        field: "deleted",
        operation: "EQ",
        value: `${filters.status !== "active"}`
      });
    }

    if (filters.stock !== "all") {
      const operation = { positive: "GT", zero: "EQ", negative: "LT" }[
        filters.stock
      ];
      repositoryFilters.push({ field: "stock_count", operation, value: "0" });
    }

    return repositoryFilters;
  }

  async getCategoriesByName(name) {
    const result = await CategoryRepository.searchByName(name);
    return result.success ? result.data.map(category => category.uuid) : [];
  }

  resetCheckboxViewModelInfo() {
    this.enqueue(
      {
        checkedProducts: {},
        checkboxCount: 0,
        renderSelectAllButton: false
      },
      () => this.doLoadResults()
    );
  }

  onFilterChange(filters) {
    this.enqueue(
      vm => ({
        filters,
        pagination: {
          ...vm.pagination,
          currentPage: 1
        }
      }),
      () => this.resetCheckboxViewModelInfo()
    );
  }

  onPageChange(currentPage) {
    this.enqueue(
      vm => ({
        pagination: {
          ...vm.pagination,
          currentPage
        }
      }),
      () => this.resetCheckboxViewModelInfo()
    );
  }

  onOrderChange(field, direction) {
    this.enqueue(
      vm => ({
        order: {
          field,
          direction
        },
        pagination: {
          ...vm.pagination,
          currentPage: 1
        }
      }),
      () => this.doLoadResults()
    );
  }

  onRequestDelete(product) {
    this.sendNotification({
      type: "confirmation",
      title: "Você tem certeza?",
      content: `Tem certeza que deseja excluir o produto ${product.identifier()}?`,
      actions: [
        {
          label: "Voltar"
        },
        {
          label: "Sim, quero excluir",
          type: "accent",
          callback: () => this.doDelete(product)
        }
      ]
    });
  }

  async doDelete(product) {
    this.enqueue({ loading: true });

    const repositoryResult = await ProductRepository.delete(product.uuid);

    if (repositoryResult.success) {
      this.doLoadResults();
      this.sendNotification({
        type: "success",
        content: "O produto foi excluído."
      });
    } else {
      this.sendNotification({
        type: "error",
        title: "Oops...",
        content: repositoryResult.error || "Não foi possível excluir o produto."
      });
    }

    this.enqueue({ loading: false });
  }

  async onRestore(product) {
    this.sendNotification({
      type: "confirmation",
      title: "Você tem certeza?",
      content: `Tem certeza que deseja restaurar o produto ${product.identifier()}?`,
      actions: [
        {
          label: "Cancelar"
        },
        {
          label: "Sim, quero restaurar",
          type: "accent",
          callback: async () => await this.doRestore(product)
        }
      ]
    });
  }

  async doRestore(product) {
    this.enqueue({ loading: true });
    const result = await ProductRepository.restore(product.uuid);

    if (result.success) {
      this.doLoadResults();
      this.sendNotification({
        type: "success",
        content: "O produto foi restaurado."
      });
    } else {
      this.sendNotification({
        type: "error",
        title: "Oooops...",
        content: "Não foi possível restaurar o produto."
      });
    }
    this.enqueue({ loading: false });
  }

  setPrintAmount(printAmount) {
    this.enqueue({ printAmount });
  }

  resetPrintAmount() {
    this.enqueue({ printAmount: 1 });
  }

  setLoading(value) {
    const { printAmount } = this.viewModel;
    if (printAmount > 1) {
      this.enqueue({ loading: value });
    } else if (printAmount === 0) {
      this.sendNotification({
        type: "error",
        title: "Oooops...",
        content: "Informe um valor maior que 0"
      });
    }
  }

  onCheckBoxEnqueue(checkedProducts) {
    this.enqueue({ checkedProducts, allProductsSelected: false }, () =>
      this.checkSelectedCheckboxes(this.viewModel.checkedProducts)
    );
  }

  onSingleCheckBoxEnqueue(checkedProducts) {
    this.enqueue(
      vm => ({
        checkedProducts: {
          ...vm.checkedProducts,
          ...checkedProducts
        },
        allProductsSelected: false
      }),
      () => this.checkSelectedCheckboxes()
    );
  }

  checkSelectedCheckboxes() {
    const total = parseInt(this.viewModel.pagination.total, 10);
    const checkboxes = this.viewModel.checkedProducts;
    const checkboxArray = Object.values(checkboxes);
    const checkboxCount = checkboxArray.filter(bool => {
      return bool;
    });

    if (checkboxCount.length === 25 || checkboxCount.length === total) {
      this.showSelectAllProductsButton(true);
    } else {
      this.showSelectAllProductsButton(false);
    }

    this.enqueue({ checkboxCount: checkboxCount.length });
  }

  showSelectAllProductsButton(bool) {
    const renderSelectAllButton = bool ? true : false;
    this.enqueue({ renderSelectAllButton });
  }

  handleModifyAction(action) {
    const wordAction = {
      delete: "excluir",
      restore: "restaurar"
    };
    const allSelected = this.viewModel.allProductsSelected || false;
    const message = `Tem certeza que deseja ${
      wordAction[action]
    } todos os produtos ${allSelected ? "da empresa" : "selecionados"}?`;
    this.sendNotification({
      type: "confirmation",
      title: "Você tem certeza?",
      content: message,
      actions: [
        {
          label: "Cancelar"
        },
        {
          label: "Sim, eu tenho certeza",
          type: "accent",
          callback: async () => await this.modifyCheckedProducts(action)
        }
      ]
    });
  }

  handleSelectAllButton() {
    const totalProducts = this.viewModel.pagination.total;
    this.enqueue({ checkboxCount: totalProducts, allProductsSelected: true });
  }

  async modifyCheckedProducts(action) {
    this.enqueue({ loading: true });
    const { checkedProducts, allProductsSelected } = this.viewModel;
    const currentUser = await this.mRuntime.get("currentUser");
    const { company_uuid } = currentUser;

    const message = {
      success: {
        delete: "excluídos",
        restore: "restaurados"
      },
      error: {
        delete: "excluir",
        restore: "restaurar"
      }
    };

    if (allProductsSelected) {
      let result = "";
      if (action === "delete") {
        result = await ProductRepository.deleteAll(company_uuid);
      }

      if (action === "restore") {
        result = await ProductRepository.restoreAll(company_uuid);
      }

      if (result.success) {
        this.sendNotification({
          type: "success",
          content: `Os produtos da empresa foram ${message.success[action]} com sucesso!`
        });
      } else {
        this.sendNotification({
          type: "error",
          title: "Oops...",
          content: `Não foi possível ${message.error[action]} os produtos. Tente novamente mais tarde ou contate o suporte.`
        });
      }
    } else {
      const parsedProducts = [];

      _.mapKeys(checkedProducts, (value, key) => {
        const condition = {
          delete: true,
          restore: false
        };

        if (value) {
          parsedProducts.push({ uuid: key, deleted: condition[action] });
        }
      });

      const result = await ProductRepository.updateMany(
        company_uuid,
        parsedProducts
      );
      if (result.success) {
        this.sendNotification({
          type: "success",
          content: `Os produtos selecionados foram ${message.success[action]} com sucesso!`
        });
      } else {
        this.sendNotification({
          type: "error",
          title: "Oops...",
          content: `Não foi possível ${message.error[action]} os produtos. Tente novamente mais tarde ou contate o suporte.`
        });
      }
    }
    this.enqueue(vm => ({
      loading: false,
      pagination: {
        ...vm.pagination,
        currentPage: 1
      }
    }));
    this.resetCheckboxViewModelInfo();
  }

  onHandleBatchCategoryDialog(action) {
    const boolAction = {
      open: true,
      close: false
    };
    const batchCategoryDialogOpen = boolAction[action];
    this.enqueue({ batchCategoryDialogOpen });
  }

  onSelectBatchCategory(category) {
    let message = `Tem certeza que deseja alterar a categoria de todos os produtos selecionados para a categoria ${category.name}?`;
    if (!category.uuid) {
      message = `Tem certeza que deseja remover a categoria de todos os produtos selecionados?`;
    }
    this.sendNotification({
      type: "confirmation",
      title: "Você tem certeza?",
      content: message,
      actions: [
        {
          label: "Cancelar"
        },
        {
          label: "Sim, eu tenho certeza",
          type: "accent",
          callback: async () => await this.onConfirmBatchCategory(category)
        }
      ]
    });
  }

  async onConfirmBatchCategory(category) {
    this.enqueue({ loading: true });

    const currentUser = await this.mRuntime.get("currentUser");
    const { company_uuid } = currentUser;
    const { checkedProducts } = this.viewModel;
    const categoryUuid = category.uuid;
    const parsedProducts = [];

    _.mapKeys(checkedProducts, (value, key) => {
      if (value) {
        parsedProducts.push({ uuid: key, category_uuid: categoryUuid });
      }
    });

    const result = await ProductRepository.updateMany(
      company_uuid,
      parsedProducts
    );
    if (result.success) {
      this.sendNotification({
        type: "success",
        content: `Os produtos selecionados foram atualizados com sucesso!`
      });
      this.resetCheckboxViewModelInfo();
    } else {
      this.sendNotification({
        type: "error",
        title: "Oops...",
        content: `Não foi possível atualizar os produtos. Tente novamente mais tarde ou contate o suporte.`
      });
    }
  }

  async verifyCrt() {
    const currentUser = await this.mRuntime.get("currentUser");
    const { fiscal_informations } = currentUser.company;

    if (!fiscal_informations.crt_code) {
      this.sendNotification({
        type: "confirmation",
        title: "Deseja continuar?",
        content: `O CRT - Código de Regime Tributário da sua empresa não foi informado, \nfavor informá-lo para prosseguir com o cadastro do produto. Deseja continuar?`,
        actions: [
          {
            label: "Não",
            callback: () => this.onHandleBatchFiscalInformationsDialog("close")
          },
          {
            label: "Sim",
            type: "accent",
            callback: () => this.navigateTo("app.accountant")
          }
        ]
      });
    } else {
      this.onHandleBatchFiscalInformationsDialog("open");
    }
  }

  onHandleBatchFiscalInformationsDialog(action) {
    const boolAction = {
      open: true,
      close: false
    };
    const batchFiscalInformationsDialogOpen = boolAction[action];
    this.enqueue({ batchFiscalInformationsDialogOpen });
  }

  onSelectBatchFiscalInformations(fiscal_informations) {
    let message = `Tem certeza que deseja alterar as informações fiscais de todos os produtos selecionados?`;
    this.sendNotification({
      type: "confirmation",
      title: "Você tem certeza?",
      content: message,
      actions: [
        {
          label: "Cancelar"
        },
        {
          label: "Sim, eu tenho certeza",
          type: "accent",
          callback: async () =>
            await this.onConfirmBatchFiscalInformations(fiscal_informations)
        }
      ]
    });
  }

  async onConfirmBatchFiscalInformations(fiscal_informations) {
    this.enqueue({ loading: true });

    const currentUser = await this.mRuntime.get("currentUser");
    const { company_uuid } = currentUser;
    const { checkedProducts } = this.viewModel;
    const parsedProducts = [];

    _.mapKeys(checkedProducts, (value, key) => {
      if (value) {
        parsedProducts.push({ uuid: key, taxes: fiscal_informations });
      }
    });

    const result = await ProductRepository.updateMany(
      company_uuid,
      parsedProducts
    );
    if (result.success) {
      this.sendNotification({
        type: "success",
        content: `Os produtos selecionados foram atualizados com sucesso!`
      });
      this.resetCheckboxViewModelInfo();
    } else {
      this.sendNotification({
        type: "error",
        title: "Oops...",
        content: `Não foi possível atualizar os produtos. Tente novamente mais tarde ou contate o suporte.`
      });
    }
    this.onHandleBatchFiscalInformationsDialog("close");
    this.enqueue({ loading: false });
  }

  getCategoryFilters(value) {
    return [
      { field: "name", operation: "LIKE", value },
      { field: "deleted", operation: "EQ", value: "false" }
    ];
  }
}

export default DataTablePresenter;



// WEBPACK FOOTER //
// ./src/components/app/registration/product/list/data-table/presenter.js